<template>
  <b-form v-if="groupTypes && groupTypes.length" class="settings-custom-field-block my-3" @submit.prevent="onFormSubmit">
    <b-form-group label="Name">
      <b-form-input type="text" required placeholder="Enter a field name" v-model="name"/>
    </b-form-group>

    <b-form-group label="Description">
      <b-form-input type="text" placeholder="Enter a field name" v-model="description"/>
    </b-form-group>

    <b-form-group label="Type">
      <b-form-select
        :disabled="!newField"
        :options="Object.keys(validTypes).filter(el => groupTypes.includes(el)).reduce((out, el) => Object.assign(out, { [el]: validTypes[el] }), {})"
        required
        v-model="type"
      />
    </b-form-group>

    <b-form-group v-if="type === 'ENUM'" label="Options">
      <b-input-group class="mb-2" v-for="(option, i) in options" :key="`${i}-${option}`">
        <b-form-input :value="option" @input="value => options[i] = value"/>
        <b-input-group-append>
          <b-button @click.prevent="() => options.splice(i, 1)" variant="outline-primary"><font-awesome-icon icon="minus"/></b-button>
        </b-input-group-append>
      </b-input-group>
      <b-input-group>
        <b-form-input disabled placeholder="Click + to add an option"/>
        <b-input-group-append>
          <b-button @click.prevent="() => options.push('')" variant="outline-primary"><font-awesome-icon icon="plus"/></b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <b-form-group v-if="['JOB_SKILLS', 'JOB_DEVELOPMENT_SKILLS', 'JOB_TITLES', 'JOB_BUSINESS_AREA', 'JOB_SENIORITY'].includes(type)" label="Import Options">
      <b-row>
        <b-col>
          <p>If you do not upload a list of items, a default list will be used as options.</p>
          <p><a href="#">Download an example file here</a></p>
        </b-col>
      </b-row>
      <b-row>
        <!-- b-col>
          <b-form-input type="number" placeholder="Number of header rows" v-model="headerRowsCount" />
        </b-col -->
        <b-col>
          <b-form-file
            placeholder="Upload a xls, xlsx or csv file..."
            drop-placeholder="Drop a xls, xlsx or csv file here..."
            accept=".xls, .xlsx, .csv"
            @change="importFile"
          ></b-form-file>
        </b-col>
      </b-row>
      <b-row v-if="options && options.length" class="mt-3">
        <b-card class="mx-3 w-100">
          <b-row>
            <b-col class="pb-3"><b>Options Preview</b></b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-select
                class="mb-3"
                :options="options"
                v-model="previewSelectedOption"></b-form-select>
              <b-form-select
                v-if="previewSelectedOption && Array.isArray(options.find(el => el.text === previewSelectedOption).options)"
                class="mb-3"
                :options="options.find(el => el.text === previewSelectedOption).options"></b-form-select>
            </b-col>
          </b-row>
        </b-card>
      </b-row>
    </b-form-group>

    <b-form-group v-if="departments.length" label="Departments" description="Optionally restrict this custom field to users in specific departments">
      <b-form-checkbox-group v-model="departmentIDs">
        <b-form-checkbox v-for="department in departments" :key="department.id" :value="department.id">
          <font-awesome-icon :icon="department.icon"/>
          {{ department.name }}
        </b-form-checkbox>
      </b-form-checkbox-group>
    </b-form-group>

    <b-form-group>
      <b-form-checkbox class="mb-2 mr-sm-2 mb-sm-0" v-model="required">Required</b-form-checkbox>
      <b-form-text v-if="departments.length">If you select <strong>required</strong> and a <strong>department</strong> then this custom field will only be required for <strong>that department</strong></b-form-text>
    </b-form-group>

    <div class="d-flex flex-row mb-3 justify-content-between">
      <template v-if="newField">
        <b-btn @click.prevent="onFormRemove" variant="outline-primary">Remove</b-btn>
        <b-btn type="submit" variant="primary">Create</b-btn>
      </template>
      <template v-else>
        <b-btn @click.prevent="onFormRemove" variant="outline-primary">Delete</b-btn>
        <b-btn type="submit" variant="primary">Update</b-btn>
      </template>
    </div>
  </b-form>
</template>

<script>
import Excel from 'exceljs';

export default {
  props: {
    value: Object,
    newField: {
      type: Boolean,
      default: false,
    },
    departments: {
      type: Array,
      default: () => [],
    },
    groupKey: {
      type: String,
      default: null,
    },
    groupTypes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    name: null,
    description: null,
    departmentIDs: [],
    options: null,
    required: null,
    type: null,
    group: null,
    headerRowsCount: 1,
    previewSelectedOption: null,
  }),
  created() {
    const { group, type, name, description, departments, options, required } = this.value;
    this.type = type || 'STRING';
    this.group = group || this.groupKey;
    this.name = name;
    this.description = description;
    if (departments && departments.length) {
      this.departmentIDs = departments.map(({ id }) => id);
    }
    if (([ 'ENUM', 'JOB_SKILLS', 'JOB_TITLES', 'JOB_DEVELOPMENT_SKILLS', 'JOB_BUSINESS_AREA', 'JOB_SENIORITY' ].includes(type)) && options) {
      this.options = JSON.parse(options);
      console.log('Options: ', this.options);
    } else {
      this.options = [];
    }
    this.required = required === true;
  },
  computed: {
    key() {
      return this.value && this.value.key ? this.value.key : null;
    },
    validTypes: () => ({
      STRING: 'Text',
      ENUM: 'Select',
      RANGE: 'Range Slider',
      JOB_SKILLS: 'Job Skills',
      JOB_DEVELOPMENT_SKILLS: 'Development Skills',
      JOB_TITLES: 'Job Titles',
      JOB_BUSINESS_AREA: 'Business Area',
      JOB_SENIORITY: 'Seniority',
      QUALIFICATIONS: 'Qualifications',
      LANGUAGES: 'Languages',
      LOCATION: 'Location',
      TEXTAREA: 'Text',
      CHECKBOX: 'Checkbox',
      CV_UPLOAD: 'CV Upload',
    }),
  },
  watch: {
    type(_, oldVal) {
      if (oldVal) {
        this.options = [];
        this.previewSelectedOption = null;
      }
    },
  },
  methods: {
    onFormRemove() {
      if (this.newField || confirm('Are you sure?') === true) {
        this.$emit('remove');
      }
    },
    importFile(event) {
      const workbook = new Excel.Workbook();
      const reader = new FileReader();
      const file = event.target.files[0];
      const { headerRowsCount } = this;
      const fileExtension = file.name.split('.').pop();

      reader.readAsArrayBuffer(file);
      reader.onload = async () => {
        try {
          const buffer = reader.result;
          this.$assert(
            workbook[fileExtension] && typeof workbook[fileExtension].load === 'function',
            new Error('File type is not allowed.')
          );
          const wb = await workbook[fileExtension].load(buffer);
          const options = [];
          wb.eachSheet((sheet, id) => {
            if (sheet.actualColumnCount === 1) {
              sheet.eachRow((row, rowIndex) => {
                const values = row.values.filter(el => el && typeof el === 'string');
                if (values[0] && rowIndex > headerRowsCount) {
                  options.push(values[0]);
                }
              });
            } else if (sheet.actualColumnCount === 2) {
              sheet.eachRow((row, rowIndex) => {
                const values = row.values.filter(el => el && typeof el === 'string');
                if (values[0] && values[1] && rowIndex > headerRowsCount) {
                  const i = options.findIndex(el => el.text === values[0]);
                  if (i >= 0) {
                    options[i].options.push(values[1]);
                  } else {
                    options.push({
                      text: values[0],
                      options: [ values[1] ],
                    });
                  }
                }
              });
            } else {
              throw new Error('Error with parsing excel file. Number of columns must be 1 or 2.');
            }
          });
          this.options = options;
          console.log(this.options);
        } catch (error) {
          this.$log.error(error);
          this.$notifyErr(error);
        }
      };
    },
    onFormSubmit() {
      this.$emit('save', {
        type: this.type,
        group: this.group,
        key: this.newField ? null : this.key,
        name: this.name,
        description: this.description,
        departments: this.departmentIDs.length ? { id_in: this.departmentIDs } : null,
        options: this.options && this.options.length ? JSON.stringify(this.options) : null,
        required: this.required,
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/settings/CustomFieldBlock.vue